import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ViewIds } from '@ardoq/api-types';
import { ChartWindowState } from '@ardoq/graph';
import { BubbleChartViewProperties } from './types';
import { BubbleChartViewContainer } from './atoms';
import { BubbleChart as BubbleChartBase } from './BubbleChart';
import { BUBBLE_CHART_SCALE_LIMITS } from './zoomControls/utils';

const VIEW_ID = ViewIds.BUBBLE;
const Container = styled.div`
  flex: 1;
  min-height: 100px;
  min-width: 100px;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const BubbleChartView = ({
  viewInstanceId,
  viewSettings,
  windowCenter,
  windowScale,
  hoverState,
  focusState,
  clientRect,
  dimensions,
  chartRenderingParameters,
  EmptyStateComponent,
  ViewLegendComponent,
  ZoomControlsComponent,
  emptyStateData,
  bubbleChartCommands,
  viewInterface: { contextMenuName, trackingCallbacks },
}: BubbleChartViewProperties) => {
  const contentPaneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bubbleChartCommands.setContentContainer(() => contentPaneRef.current);
  }, [bubbleChartCommands]);

  const { isLegendActive } = viewSettings;

  const setWindowState = useCallback(
    (args: ChartWindowState) =>
      args.windowScale === windowScale
        ? bubbleChartCommands.setCenter(args.windowCenter, viewInstanceId)
        : bubbleChartCommands.setScale(args, viewInstanceId),
    [windowScale, viewInstanceId, bubbleChartCommands]
  );

  const onPanEnd = useCallback(
    () => bubbleChartCommands.panSettle(viewInstanceId),
    [viewInstanceId, bubbleChartCommands]
  );

  const viewLegendContent = chartRenderingParameters?.viewLegendContent;
  const zoomControlsProps = chartRenderingParameters?.zoomControlsProps;

  useEffect(() => {
    if (chartRenderingParameters) {
      trackingCallbacks?.onRendered?.();
    }
  }, [chartRenderingParameters, trackingCallbacks]);

  return (
    <BubbleChartViewContainer
      className={`tab-pane ${VIEW_ID}Tab active`}
      data-context-menu={contextMenuName}
    >
      {zoomControlsProps && ZoomControlsComponent ? (
        <ZoomControlsComponent {...zoomControlsProps} />
      ) : null}
      {emptyStateData.isViewEmpty ? (
        EmptyStateComponent && emptyStateData ? (
          <EmptyStateComponent {...emptyStateData} />
        ) : null
      ) : (
        <Container ref={contentPaneRef}>
          {chartRenderingParameters && dimensions.chart.width ? (
            <BubbleChartBase
              windowCenter={windowCenter}
              windowScale={windowScale}
              setWindow={setWindowState}
              onPanEnd={onPanEnd}
              scaleLimits={BUBBLE_CHART_SCALE_LIMITS}
              selectComponent={bubbleChartCommands.selectComponent}
              viewInstanceId={viewInstanceId}
              hoverState={hoverState}
              focusState={focusState}
              clientRect={clientRect}
              dimensions={dimensions}
              chartRenderingParameters={chartRenderingParameters}
              bubbleChartCommands={bubbleChartCommands}
            />
          ) : null}
          {viewLegendContent && ViewLegendComponent ? (
            <ViewLegendComponent
              isLegendActive={isLegendActive}
              {...viewLegendContent}
            />
          ) : null}
        </Container>
      )}
    </BubbleChartViewContainer>
  );
};

export { BubbleChartView };
